import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type React from 'react';
import type { ReactNode } from 'react';
import { ErrorProvider } from './ErrorProvider';
import { ErrorBoundary } from '@/shared/components/ahkinia/ErrorBoundary/ErrorBoundary';

interface ProviderProps {
  children: ReactNode;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,

      retry: (failureCount, error) => {
        // No reintentar en errores 404 o 403
        if (error instanceof Error) {
          const message = error.message.toLowerCase();
          if (message.includes('404') || message.includes('403')) {
            return false;
          }
        }

        // Máximo 3 reintentosS
        return failureCount < 1;
      },
      // Refetch automático cuando la ventana gana foco
      refetchOnWindowFocus: false,

      // Refetch automático cuando se reconecta
      refetchOnReconnect: false,

      // No refetch automático al montar si los datos están frescos. Debe ser `true`, no `false`:
      // `false` tampoco refetchea los datos rancios, así que una query invalidada mientras estaba
      // inactiva (p. ej. el detalle de un país tras editarlo con el modal cerrado) servía caché
      // vieja hasta recargar la página.
      refetchOnMount: true,
    },
    mutations: {
      retry: false,
    },
  },
});

export const Providers: React.FC<ProviderProps> = ({ children }) => {
  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <ErrorProvider>{children}</ErrorProvider>
      </ErrorBoundary>
    </QueryClientProvider>
  );
};
